<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import SidePanel from '../SidePanel/SidePanel.vue';
import useModal, { modals } from '../../composables/useModal';
import { useSidebarUser } from '../../composables/useSidebarUser';
import { usePermissions } from '../../composables/usePermissions';
import EditUserInfoModal from './EditUser/EditUserInfoModal.vue';
import ManageLogin from './ManageLogin.vue';
import ManageGroups from './ManageGroups.vue';
import AccountInfo from './AccountInfo.vue';
import ManageLoginModal from './EditUser/ManageLoginModal/index.vue';
import CreateLoginCardModal from '../LoginCard/CreateLoginCardModal.vue';
import ConfirmDeleteQrModal from './EditUser/ConfirmDeleteQrModal.vue';
import ManageUserGroupsModal from './EditUser/ManageUserGroupsModal.vue';
import { useLocale } from '../../composables/useLocale';
import ManageUserOrganizationsModal from './EditUser/ManageUserOrganizationsModal.vue';
import UserSidePanelFooter from './UserSidePanelFooter.vue';
import DeleteUserModal from './DeleteUserModal.vue';

const { t } = useLocale();
type Props = {
  userUid: string;
  organizationUid: string;
};
const props = defineProps<Props>();
const emit = defineEmits(['close', 'userDeleted', 'userUpdated']);

const { user, isLoading, getUser } = useSidebarUser();
const { getRoleFromAssociation } = usePermissions();
const { getUserGroups, groups, isLoading: isLoadingGroups } = useSidebarUser();
const { open: openEditUserModal } = useModal(modals.EDIT_USER);
const { open: openManageUserLoginInfoModal, close: closeManageUserLoginInfoModal } = useModal(
  modals.MANAGE_USER_LOGIN_INFO,
);
const manageLoginModal = ref<InstanceType<typeof ManageLoginModal> | null>(null);
const { open: openCreateLoginCardModal } = useModal(modals.CREATE_LOGIN_CARD);
const { close: closeRemoveQrCodeConfirmation } = useModal(modals.CONFIRM_DELETE_USER_QR_CODE);
const { close: closeManageContentSettings } = useModal(modals.MANAGE_CONTENT_SETTINGS);

const onOpenCreateLoginCardModal = () => {
  if (!user.value) {
    return;
  }
  closeManageUserLoginInfoModal();
  openCreateLoginCardModal();
};

const sidePanel = ref();

watch(
  () => [props.userUid, props.organizationUid],
  async (newArgs, prevArgs) => {
    const [newUid, newSchoolUid] = newArgs;
    const [prevUid, prevSchoolUid] = prevArgs ?? [];

    const isUserChanged = newUid !== prevUid;

    const promises = [];
    if (newUid && isUserChanged) {
      promises.push(getUser(newUid));
    }

    if (isUserChanged || newSchoolUid !== prevSchoolUid) {
      if (newUid) {
        promises.push(getUserGroups(newUid, newSchoolUid));
      }
    }

    await Promise.all(promises);
  },
  { immediate: true },
);

const schoolName = computed(() => {
  return user.value?.associations.find((e) => e.schoolUnit.uid === props.organizationUid)?.schoolUnit.name ?? '';
});

const title = computed(() => user.value?.name ?? '');
// An administrator reaches the client as an employee carrying `isAdministrator`, so the role has
// to be derived. Reading it straight off the association calls every administrator staff.
const role = computed(() => {
  const association = user.value?.associations.find((e) => e.schoolUnit.uid === props.organizationUid);
  return association ? getRoleFromAssociation(association) : '';
});
const subtitle = computed(() => {
  return t(`lcp.role_in_school`, {
    role: t(`lcp.role_${role.value?.toLowerCase()}`),
    school: schoolName.value?.toLowerCase(),
  });
});
const userUpdated = () => {
  // make typescript happy
  if (!user.value || !user.value.uid) {
    return;
  }

  getUser(user.value.uid, true);
  emit('userUpdated', user.value?.uid);
};
const userDeleted = () => {
  emit('userDeleted');
  emit('close');
};

const onDeleteQrCode = () => {
  closeRemoveQrCodeConfirmation();
  manageLoginModal.value?.removeQrCode();
  openManageUserLoginInfoModal();
};
const onCancelDeleteQrCode = () => {
  closeRemoveQrCodeConfirmation();
  openManageUserLoginInfoModal();
};
</script>

<template>
  <SidePanel
    ref="sidePanel"
    selected-entity="user"
    :selected-entity-uid="userUid"
    :title="title"
    :subtitle="subtitle"
    :initials="user ? user.firstName?.charAt(0) + user.lastName?.charAt(0) : ''"
    icon="mdi-account"
    :is-loading="isLoading"
    :title-action="openEditUserModal"
    :on-close="() => emit('close')"
  >
    <ManageLogin
      :is-loading="isLoading"
      :identifiers="user?.identifiers ?? []"
      :username="user?.userName ?? ''"
      :qrCodeEnabled="!!user?.qrEnabled"
    />
    <ManageGroups v-if="user" :is-loading="isLoadingGroups" :user="user" :memberships="groups?.data ?? []" />
    <slot name="settings" :user="user" :organization-uid="organizationUid"></slot>
    <AccountInfo
      :is-loading="isLoading"
      :created-at="user?.createdAt ?? null"
      :updated-at="user?.updatedAt ?? null"
      :last-active-at="user?.lastActiveAt ?? null"
    />
    <template #append>
      <UserSidePanelFooter v-if="user" :user="user" />
    </template>
  </SidePanel>
  <EditUserInfoModal
    v-if="user"
    :name="modals.EDIT_USER"
    :organization-uid="organizationUid!"
    :user="user"
    @userUpdated="userUpdated"
  />
  <ManageLoginModal
    v-if="user"
    ref="manageLoginModal"
    :action="closeManageUserLoginInfoModal"
    :name="modals.MANAGE_USER_LOGIN_INFO"
    :secondaryAction="onOpenCreateLoginCardModal"
    :secondaryActionLabel="t('lcp.create_new_login_card')"
    secondaryActionColor="secondary"
    secondaryActionVariant="tonal"
  />
  <ManageUserGroupsModal
    v-if="user && groups"
    :user="user"
    :name="modals.MANAGE_USER_GROUPS"
    :organization-uid="organizationUid"
    :memberships="groups"
  />
  <DeleteUserModal v-if="user" :user="user" @deleted="userDeleted" />
  <ManageUserOrganizationsModal v-if="user" :user="user" :name="modals.MANAGE_USER_ORGANIZATIONS" />
  <ConfirmDeleteQrModal
    v-if="user"
    :name="modals.CONFIRM_DELETE_USER_QR_CODE"
    @delete="onDeleteQrCode"
    @cancel="onCancelDeleteQrCode"
  />
  <CreateLoginCardModal v-if="user" :name="modals.CREATE_LOGIN_CARD" :user="user!" @user-updated="userUpdated" />
  <slot
    v-if="user"
    name="settings-modal"
    :user="user"
    :organization-uid="organizationUid"
    :close="closeManageContentSettings"
  ></slot>
</template>

<style scoped lang="scss"></style>
